import { Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { BreadcrumbComponent, BreadcrumbItemComponent } from '@coreui/angular';
import { TranslatePipe } from '@ngx-translate/core';

export interface Crumb {
    label: string;
    url?: string;
}

@Component({
    selector: 'app-breadcrumb',
    standalone: true,
    imports: [
        AsyncPipe, 
        BreadcrumbComponent, 
        BreadcrumbItemComponent, 
        TranslatePipe
    ],
    template: `
        <c-breadcrumb class="my-0">
            @for (crumb of crumbs$ | async; track $index; let last = $last) {
                <c-breadcrumb-item [active]="last" [url]="last ? undefined : crumb.url">
                    {{ crumb.label|translate }}
                </c-breadcrumb-item>
            }
        </c-breadcrumb>
    `
})

export class AppBreadcrumbComponent {
    private router = inject(Router);

    crumbs$ = this.router.events.pipe(
        filter(e => e instanceof NavigationEnd),
        startWith(null),
        map(() => this.build())
    );

    private build(): Crumb[] {
        let snap = this.router.routerState.snapshot.root;
        const params: Record<string, string> = { ...snap.params };
        while (snap.firstChild) {
            snap = snap.firstChild;
            Object.assign(params, snap.params);
        }

        const raw = snap.data['breadcrumb'] ?? snap.data['title'];
        const list = Array.isArray(raw) ? raw : raw ? [raw] : [];

        const items: Crumb[] = list.map((i: string | Crumb) => {
            const crumb = typeof i === 'string' ? { label: i } : i;
            return {
                label: crumb.label,
                url: crumb.url?.replace(/:(\w+)/g, (_, key) => params[key] ?? `:${key}`)
            };
        });

        return [{ label: 'general.home', url: '/dashboard' }, ...items];
    }
}